import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Euro, MessageSquareText, Loader2, CheckCircle } from "lucide-react";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { useState } from "react";

import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import { ItineraryActions } from "./ItineraryActions";

import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { ItineraryStatus } from "../builder/models/itinerary.model";
import { useI18n } from '@/hooks/useI18n';
import { useModal } from "@/contexts/ModalContext";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";

interface ItineraryCardProps {
  itinerary: DashboardItinerary;
  className?: string;
  onEdit?: (itineraryId: string) => void;
  onDelete?: (itineraryId: string) => void;
  onDuplicate?: (itineraryId: string) => void;
  onViewTravelRequest?: (travelRequestId: string) => void;
  onViewItinerary?: (itineraryId: string) => void;
  onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
  travelRequests?: DashboardTravelRequest[];
  isAdmin?: boolean;
  isTraveler?: boolean;
  userRole?: string;
}

export const ItineraryCard = ({
  itinerary,
  className,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onViewItinerary,
  onUpdateStatus,
  isAdmin,
  isTraveler,
  userRole,
}: ItineraryCardProps) => {
  const { t } = useI18n();
  const { openModal, currentModal } = useModal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGeneratingPDFBE, setIsGeneratingPDFBE] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [isGeneratingPaymentLink, setIsGeneratingPaymentLink] = useState(false);
  const [paymentLinkSuccess, setPaymentLinkSuccess] = useState(false);

  const handleViewNotes = () => {
    openModal('notes', {
      note: itinerary.note
    });
  };

  const handleCardClick = () => {
    // Prevent opening details modal if any modal is currently open
    if (currentModal) return;
    
    openModal('itinerary-details', {
      itinerary,
      onViewItinerary
    });
  };

  // Get guest count
  const guestCount = itinerary.totalGuests || 1;

  return (
    <Card
      className={cn(
        "border border-gray-300 rounded-2xl overflow-hidden transition-all cursor-pointer",
        isDropdownOpen
          ? "-translate-y-2 scale-105 shadow-xl"
          : "hover:-translate-y-2 hover:scale-105 hover:shadow-xl",
        className,
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-0 h-full flex flex-col relative">
        {/* PDF generating overlay */}
        <div
          className={cn(
            "absolute inset-0 z-20 rounded-2xl flex flex-col items-center justify-center gap-3 transition-all duration-700",
            isGeneratingPDFBE || pdfSuccess
              ? "opacity-100"
              : "opacity-0 pointer-events-none",
            pdfSuccess ? "bg-green-50" : "bg-white/80 backdrop-blur-sm",
          )}
        >
          {isGeneratingPDFBE && (
            <>
              <Loader2 className="h-10 w-10 text-gray-400 animate-spin" />
              <span className="font-medium text-gray-500">
                {t('itinerary.generatingPDF')}
              </span>
            </>
          )}
          {pdfSuccess && (
            <>
              <div className="animate-pop-in">
                <CheckCircle
                  className="h-9 w-9 text-green-500"
                  strokeWidth={1.5}
                />
              </div>
              <span
                className="font-semibold text-green-700 animate-pop-in"
                style={{ animationDelay: "80ms" }}
              >
                {t('itinerary.pdfReady')}
              </span>
            </>
          )}
        </div>

        {/* Payment link generating overlay */}
        <div
          className={cn(
            "absolute inset-0 z-20 rounded-2xl flex flex-col items-center justify-center gap-3 transition-all duration-700",
            isGeneratingPaymentLink || paymentLinkSuccess
              ? "opacity-100"
              : "opacity-0 pointer-events-none",
            paymentLinkSuccess ? "bg-green-50" : "bg-white/80 backdrop-blur-sm",
          )}
        >
          {isGeneratingPaymentLink && (
            <>
              <Loader2 className="h-10 w-10 text-gray-400 animate-spin" />
              <span className="font-medium text-gray-500">
                {t('itinerary.generatingPaymentLink')}
              </span>
            </>
          )}
          {paymentLinkSuccess && (
            <>
              <div className="animate-pop-in">
                <CheckCircle
                  className="h-9 w-9 text-green-500"
                  strokeWidth={1.5}
                />
              </div>
              <span
                className="font-semibold text-green-700 animate-pop-in"
                style={{ animationDelay: "80ms" }}
              >
                {t('itinerary.paymentLinkCopied')}
              </span>
            </>
          )}
        </div>

        {/* Image Header */}
        <div className="relative w-full h-[150px]">
          {/* Background Image */}
          <div
            className={cn(
              "absolute inset-0 bg-cover bg-center transition-all rounded-tl rounded-tr",
              itinerary.image ? "bg-gray-200" : "bg-button-primary",
              itinerary.isBlurred && "blur-[2px] brightness-90",
            )}
            style={{
              backgroundImage: itinerary.image
                ? `url(${getStorageThumbnailUrl(itinerary.image, { noTransform: true })})`
                : undefined,
            }}
          />

          {/* Logo placeholder quando non c'è immagine */}
          {!itinerary.image && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <img
                src="/assets/images/light_extended.png"
                alt="Place2Be Logo"
                className="max-w-[70%] max-h-[70%] object-contain"
              />
            </div>
          )}

          {/* Badge e messaggi per status */}
          <div>
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              <ItineraryStatusBadge
                status={itinerary.detailedStatus}
                hideDraftQuote={true}
                userRole={userRole}
              />
            </div>
            <div className="absolute bottom-3 left-3 flex flex-col gap-2">
              {/* Bottone review solo per pending e admin */}
              {itinerary.isPending && isAdmin && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-500 bg-gray-100 hover:bg-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.(itinerary.id);
                    }}
                  >
                    {t("itinerary.takeReview")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex gap-2">
            {itinerary.note &&
              (!isTraveler || itinerary.status === "request_changes") && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
                  title={t('itinerary.viewNotes')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewNotes();
                  }}
                >
                  <MessageSquareText className="h-4 w-4 text-gray-800" />
                </Button>
              )}

            {/* Menu Button */}
            <ItineraryActions
              itinerary={itinerary}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onViewTravelRequest={onViewTravelRequest}
              onViewItinerary={onViewItinerary}
              onUpdateStatus={onUpdateStatus}
              isAdmin={isAdmin}
              isTraveler={isTraveler}
              userRole={userRole}
              variant="card"
              onOpenChange={setIsDropdownOpen}
              onPdfStateChange={(isGenerating, isSuccess) => {
                setIsGeneratingPDFBE(isGenerating);
                setPdfSuccess(isSuccess);
              }}
              onPaymentLinkStateChange={(isGenerating, isSuccess) => {
                setIsGeneratingPaymentLink(isGenerating);
                setPaymentLinkSuccess(isSuccess);
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col">
          {/* Title */}
          <h4 className="text-black mb-4 line-clamp-1">
            {itinerary.title || t("itinerary.untitledItinerary")}
          </h4>

          {/* Trip Info Row */}
          <div className="flex items-center justify-between mb-4 text-sm">
            {/* Dates */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">
                {formatTravelDates(itinerary.startDate, itinerary.endDate)}
              </span>
            </div>

            {/* People */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">{guestCount}</span>
            </div>

            {/* Total */}
            <div className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">{itinerary.total}</span>
            </div>
          </div>

          {/* User Info */}
          {itinerary && (
            <div className="flex items-center justify-between gap-2">
              {/* Contact info */}
              {isTraveler ? (
                itinerary.assignedAdvisor ? (
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback
                        className={`text-sm text-white ${getAvatarColor(itinerary.assignedAdvisor.email)}`}
                      >
                        {getInitials(
                          itinerary.assignedAdvisor.firstName,
                          itinerary.assignedAdvisor.lastName,
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-gray-900 font-medium">
                      {t('itinerary.assignedBy')} {itinerary.assignedAdvisor.firstName}{" "}
                      {itinerary.assignedAdvisor.lastName}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400">{t('itinerary.noDesignerAssigned')}</span>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback
                      className={`text-sm text-white ${getAvatarColor(itinerary.contactEmail)}`}
                    >
                      {getInitials(
                        itinerary.contactFullName.split(" ")[0],
                        itinerary.contactFullName.split(" ").slice(-1)[0],
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-gray-900 font-medium">
                    {itinerary.contactFullName}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
